import React, {useEffect, useMemo, useState} from "react";
import {toast} from "react-toastify";
import Page from "../../Components/Page";
import {Breadcrumbs} from "../../Components/Breadcrumbs";
import ConfirmationModal from "../../Components/ConfirmationModal";
import {LoadingWrapper} from "../../Components/LoadingWrapper";
import ReactTable from "../../Components/ReactTable";
import {SELECT_COLUMN} from "../../Components/ReactTable/CheckboxColumnComponent";
import RetailerIdAndName from "../../Components/RetailerIdAndName";
import {getRetailerName} from "../../../utils/localCache/retailerMapCache";
import {sortAscendingWithNullsFirst} from "../../../utils/sorting";
import {getNicelyFormattedNumber} from "../../../utils/formatUtil";
import {makeApiCall, makeApiCallPost} from "../../../Api/makeApiCall";
import {setValuesToNullIfEmptyOrUndefined} from "../../Components/ReactTable/helpers";
import {toastError, toastSuccess} from "../../../utils/toast";
import checkCircle from "../../../assets/check-circle.svg";


const columns = [
    SELECT_COLUMN,
    {
        header: 'Retailer',
        id: 'retailerIdName',
        accessorKey: 'retailerId',
        size: 200,
        cell: ({row}) => <RetailerIdAndName retailerId={row.original.retailerId}/>,
        filterFn: (row, columnIds, filterValue) => {
            const retailerId = row.original.retailerId;
            return !filterValue || Boolean(`${retailerId} ${getRetailerName(retailerId)?.toLowerCase()}`.includes(filterValue.toLowerCase()));
        },
        sortingFn: (a, b) => sortAscendingWithNullsFirst(a.original.retailerId, b.original.retailerId)
    },
    {header: "Store ID", accessorKey: "storeId"},
    {header: "Store Name", accessorKey: "storeName"},
    {header: "Current Stop Date", accessorKey: "stopDate", enableColumnFilter: false},
    {header: "Current Start Date", accessorKey: "startDate", enableColumnFilter: false}
];

function StoreParticipationBulkEdit() {
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [tableData, setTableData] = useState([]);
    const [storeNamesById, setStoreNamesById] = useState({});
    const [stopDate, setStopDate] = useState('');
    const [startDate, setStartDate] = useState('');

    function loadData() {
        setLoading(true);
        return makeApiCall(`${process.env.REACT_APP_EDP_API_BASE_URL}/storeParticipation/?add_retailer_column=true`).then((result) => {
            const mappedData = (result.data || []).map((row) => ({
                index: row.index,
                storeId: row.storeId,
                retailerId: row.Retailer?.retailerId,
                stopDate: row.stopDate,
                startDate: row.startDate
            }));
            setTableData(mappedData);
            setLoading(false);
        });
    }

    useEffect(() => {
        loadData();
        makeApiCall(`${process.env.REACT_APP_EDP_API_BASE_URL}/stores/`).then((storesResult) => {
            setStoreNamesById(Object.fromEntries((storesResult.data || []).map(store => [store.storeId, store.storeName])));
        });
    }, []);

    const tableDataWithStoreNames = useMemo(() => tableData.map(row => ({
        ...row,
        storeName: storeNamesById[row.storeId]
    })), [tableData, storeNamesById]);

    async function submitSelectedRows(selectedRows) {
        // A record with no stop date and no start date isn't a valid participation window, so clearing
        // both fields removes the record instead of saving it as blank.
        const clearingBothDates = !stopDate && !startDate;
        const singleRowAction = clearingBothDates ? 'delete' : 'update';
        setSubmitting(true);
        const responses = [];
        // Submitted one row at a time (not multiple_submits) so each request re-reads the file after the
        // previous row saved -- the same request shape every single-row edit already uses.
        for (const row of selectedRows) {
            const rowToPost = clearingBothDates
                ? {index: row.original.index}
                : setValuesToNullIfEmptyOrUndefined({
                    index: row.original.index,
                    storeId: row.original.storeId,
                    stopDate,
                    startDate
                });
            responses.push(await makeApiCallPost(
                `${process.env.REACT_APP_EDP_API_BASE_URL}/storeParticipation/?single_row_action=${singleRowAction}`,
                rowToPost
            ));
        }
        setSubmitting(false);
        if (responses.some(response => response?.apiError)) {
            toastError(toast, `Operation failed with api error "${JSON.stringify(responses.find(response => response?.apiError)?.apiError.message)}"`);
            return;
        }
        toastSuccess(toast, clearingBothDates
            ? `${selectedRows.length} row/s had no stop or start date and were removed.`
            : `Successfully updated ${selectedRows.length} row/s.`);
        setStopDate('');
        setStartDate('');
        loadData();
    }

    async function deleteSelectedRows(selectedRows) {
        setDeleting(true);
        const responses = [];
        for (const row of selectedRows) {
            responses.push(await makeApiCallPost(
                `${process.env.REACT_APP_EDP_API_BASE_URL}/storeParticipation/?single_row_action=delete`,
                {index: row.original.index}
            ));
        }
        setDeleting(false);
        setDeleteModalVisible(false);
        if (responses.some(response => response?.apiError)) {
            toastError(toast, `Operation failed with api error "${JSON.stringify(responses.find(response => response?.apiError)?.apiError.message)}"`);
            return;
        }
        toastSuccess(toast, `Successfully deleted ${selectedRows.length} row/s.`);
        loadData();
    }

    return <Page name={"Bulk Edit Store Participation"}
                breadCrumbs={<Breadcrumbs items={[
                    {label: "Configurations"},
                    {label: "Store"},
                    {label: "Store Participation", to: "/storeParticipation"},
                    {label: "Bulk Edit"}
                ]}/>}
    >
        <LoadingWrapper loading={loading}>
            <div className={'store-participation-bulk-add-date-fields'}>
                <label htmlFor={'store-participation-bulk-edit-stop-date'}>New Stop Date</label>
                <input
                    id={'store-participation-bulk-edit-stop-date'}
                    data-testid={'store-participation-bulk-edit-stop-date'}
                    type={'date'}
                    className={'edit-form-date-input-field'}
                    value={stopDate}
                    onChange={event => setStopDate(event.target.value)}
                />
                <label htmlFor={'store-participation-bulk-edit-start-date'}>New Start Date</label>
                <input
                    id={'store-participation-bulk-edit-start-date'}
                    data-testid={'store-participation-bulk-edit-start-date'}
                    type={'date'}
                    className={'edit-form-date-input-field'}
                    value={startDate}
                    onChange={event => setStartDate(event.target.value)}
                />
            </div>
            <ReactTable
                filterable={true}
                data={tableDataWithStoreNames}
                columns={columns}
                initialState={{pagination: {pageSize: 50}}}
            >
                {
                    ({table}) => {
                        const totalRows = getNicelyFormattedNumber(table.getFilteredRowModel().rows.length);
                        const selectedRows = table.getSelectedRowModel().rows;
                        const isAllRowsSelected = table.getIsAllRowsSelected();
                        const selectAllButtonText = `Select all ${totalRows} rows`;
                        const allRowsSelectedText = `${totalRows} rows selected`;
                        const canSubmit = selectedRows.length > 0 && !submitting;
                        const canDelete = selectedRows.length > 0 && !deleting;
                        return (
                            <div className={'designation-action-buttons-container'}
                                 data-testid={"action-button-container"}>
                                {isAllRowsSelected ?
                                    <>
                                        <img alt={'status'} className={'success-image'} src={checkCircle}/>
                                        <div data-testid={'all-rows-selected'}>{allRowsSelectedText}</div>
                                    </>
                                    :
                                    <button data-testid={'select-all-rows'} type={"button"} className={"button tertiary"}
                                            onClick={() => table.toggleAllRowsSelected()}
                                    >{selectAllButtonText}</button>}
                                <button disabled={!canSubmit} type={"button"} className={"button primary"}
                                        data-testid={'submit-bulk-edit'}
                                        onClick={() => submitSelectedRows(selectedRows)}>
                                    Save new dates to selected rows
                                </button>
                                <button disabled={!canDelete} type={"button"} className={"button danger-button store-participation-delete-button"}
                                        data-testid={'delete-selected-rows'}
                                        onClick={() => setDeleteModalVisible(true)}>
                                    Delete selected rows
                                </button>
                                {deleteModalVisible && <ConfirmationModal
                                    confirmButtonClass={"danger-button"}
                                    confirmButtonText={"Delete"}
                                    headerText={`Delete ${selectedRows.length} ${selectedRows.length === 1 ? 'row' : 'rows'}?`}
                                    loading={deleting}
                                    cancellationCallback={() => setDeleteModalVisible(false)}
                                    confirmationCallback={() => deleteSelectedRows(selectedRows)}>
                                    {`Delete ${selectedRows.length} store participation ${selectedRows.length === 1 ? 'window' : 'windows'}? This can not be undone.`}
                                </ConfirmationModal>}
                            </div>
                        );
                    }
                }
            </ReactTable>
        </LoadingWrapper>
    </Page>;
}

export default StoreParticipationBulkEdit;
